import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Megaphone } from "lucide-react";
import { ConferencePromoSettingsResponse } from "./types";
import { toLocalInputValue } from "./helpers";

export function ConferencePromoBannerPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState({ active: false, text: "", ctaLabel: "", startAt: "", endAt: "" });
  const [initialized, setInitialized] = useState(false);

  const settingsQuery = useQuery<ConferencePromoSettingsResponse>({
    queryKey: ["/api/admin/settings/co-living-conference-promo"],
    queryFn: async () => {
      const res = await fetch("/api/admin/settings/co-living-conference-promo", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load promo banner settings");
      return res.json();
    },
  });

  useEffect(() => {
    if (settingsQuery.data && !initialized) {
      const { settings, envFallback } = settingsQuery.data;
      setForm({
        active: settings ? settings.active : envFallback.active,
        text: settings?.text ?? "",
        ctaLabel: settings?.ctaLabel ?? "",
        startAt: toLocalInputValue(settings?.startAt ?? null),
        endAt: toLocalInputValue(settings?.endAt ?? null),
      });
      setInitialized(true);
    }
  }, [settingsQuery.data, initialized]);

  const saveMutation = useMutation({
    mutationFn: async (data: typeof form) => {
      const startAt = data.startAt ? new Date(data.startAt) : null;
      const endAt = data.endAt ? new Date(data.endAt) : null;
      if (startAt && endAt && endAt.getTime() <= startAt.getTime()) {
        throw new Error("End must be after the start time.");
      }
      return apiRequest("PUT", "/api/admin/settings/co-living-conference-promo", {
        active: data.active,
        text: data.text.trim() ? data.text : null,
        ctaLabel: data.ctaLabel.trim() ? data.ctaLabel : null,
        startAt: startAt ? startAt.toISOString() : null,
        endAt: endAt ? endAt.toISOString() : null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings/co-living-conference-promo"] });
      queryClient.invalidateQueries({ queryKey: ["/api/co-living-conference-promo"] });
      toast({
        title: "Promo banner updated",
        description: "The /coliving-conference banner now reflects your changes.",
      });
    },
    onError: (err: any) =>
      toast({ title: "Failed to save", description: parseErrorMessage(err, "Couldn't save. Please try again."), variant: "destructive" }),
  });

  const envFallback = settingsQuery.data?.envFallback;
  const effectiveText = form.text.trim() || envFallback?.text || "";
  const effectiveCta = form.ctaLabel.trim() || envFallback?.ctaLabel || "";

  return (
    <div className="max-w-2xl space-y-4">
      <Card className="p-5 space-y-5">
        <div>
          <h3 className="font-semibold text-sm mb-1" data-testid="text-conference-promo-title">
            Co-Living Conference Promo Banner
          </h3>
          <p className="text-xs text-muted-foreground">
            Controls the promo banner shown on the public <code className="text-[11px]">/coliving-conference</code> page.
            Saved values override the legacy <code className="text-[11px]">CO_LIVING_CONFERENCE_PROMO_*</code> environment
            variables; leaving Banner Text or CTA Label blank falls back to the env value.
          </p>
        </div>

        {settingsQuery.isLoading ? (
          <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-md border px-3 py-2.5">
              <div>
                <Label className="text-sm">Banner active</Label>
                <p className="text-xs text-muted-foreground">When off, no banner is shown to visitors.</p>
              </div>
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
                data-testid="switch-conference-promo-active"
              />
            </div>

            <div>
              <Label className="text-xs">Banner Text</Label>
              <Textarea
                className="mt-1 text-sm"
                rows={3}
                placeholder={envFallback?.text || "Promo offer copy"}
                value={form.text}
                onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                data-testid="input-conference-promo-text"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Up to 500 characters. Leave blank to use the env-var default.
              </p>
            </div>

            <div>
              <Label className="text-xs">CTA Button Label</Label>
              <Input
                className="h-9 mt-1 text-sm"
                placeholder={envFallback?.ctaLabel || "Claim offer"}
                value={form.ctaLabel}
                onChange={(e) => setForm((f) => ({ ...f, ctaLabel: e.target.value }))}
                data-testid="input-conference-promo-cta"
              />
            </div>

            <div className="rounded-md border px-3 py-2.5 space-y-3">
              <div>
                <Label className="text-sm">Schedule (optional)</Label>
                <p className="text-xs text-muted-foreground">
                  Set a "show from" and/or "show until" time. Outside this window the banner is hidden even
                  when the toggle above is on. Times use your browser's local time zone.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Show from</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="datetime-local"
                      className="h-9 text-sm"
                      value={form.startAt}
                      onChange={(e) => setForm((f) => ({ ...f, startAt: e.target.value }))}
                      data-testid="input-conference-promo-start-at"
                    />
                    {form.startAt && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-9 px-2 text-xs"
                        onClick={() => setForm((f) => ({ ...f, startAt: "" }))}
                        data-testid="button-clear-conference-promo-start-at"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Show until</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="datetime-local"
                      className="h-9 text-sm"
                      value={form.endAt}
                      onChange={(e) => setForm((f) => ({ ...f, endAt: e.target.value }))}
                      data-testid="input-conference-promo-end-at"
                    />
                    {form.endAt && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-9 px-2 text-xs"
                        onClick={() => setForm((f) => ({ ...f, endAt: "" }))}
                        data-testid="button-clear-conference-promo-end-at"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-md border bg-muted/40 px-3 py-2.5 text-xs space-y-1" data-testid="preview-conference-promo">
              <p className="font-medium text-foreground">Preview</p>
              <p>
                <span className="text-muted-foreground">Active:</span>{" "}
                <span data-testid="preview-conference-promo-active">{form.active ? "Yes" : "No"}</span>
              </p>
              <p>
                <span className="text-muted-foreground">Text:</span>{" "}
                <span data-testid="preview-conference-promo-text">{effectiveText}</span>
              </p>
              <p>
                <span className="text-muted-foreground">CTA:</span>{" "}
                <span data-testid="preview-conference-promo-cta">{effectiveCta}</span>
              </p>
              {(form.startAt || form.endAt) && (
                <p data-testid="preview-conference-promo-schedule">
                  <span className="text-muted-foreground">Schedule:</span>{" "}
                  {form.startAt ? new Date(form.startAt).toLocaleString() : "—"}
                  {" → "}
                  {form.endAt ? new Date(form.endAt).toLocaleString() : "—"}
                </p>
              )}
            </div>

            <Button
              className="w-full"
              onClick={() => saveMutation.mutate(form)}
              disabled={saveMutation.isPending}
              data-testid="button-save-conference-promo"
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Megaphone className="h-4 w-4 mr-2" /> Save Banner Settings
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
